// locale-unextracted-ignore-file
import { DateRangeSelector } from '@app/components/date-range-selector/DateRangeSelector';
import {
  type DateRangePresetId,
  type DateRangeValue,
  getPresetRange,
  isCustomPreset,
  PRESET_MENU_ORDER,
} from '@app/components/date-range-selector/date-range-presets';
import { useTimezone } from '@app/core/hooks/useTimezone';
import { Box, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

/** Spanish labels for preset list (no localization in this screen). */
const PRESET_LABELS: Record<DateRangePresetId, string> = {
  today: 'Hoy',
  yesterday: 'Ayer',
  lastHour: 'Última hora',
  last24Hours: 'Últimas 24 horas',
  last7Days: 'Últimos 7 días',
  last14Days: 'Últimos 14 días',
  last30Days: 'Últimos 30 días',
  last90Days: 'Últimos 90 días',
  last180Days: 'Últimos 180 días',
  lastWeek: 'Semana pasada',
  lastMonth: 'Mes pasado',
  thisWeek: 'Esta semana',
  thisMonth: 'Este mes',
  yearToDate: 'Desde inicio del año',
  fromCustomDateUntilNow: 'Desde fecha personalizada hasta ahora...',
  customFixedDateRange: 'Rango de fechas fijo personalizado...',
};

/**
 * Resolved from/to for a single preset: either computed (fixed presets) or from current selection (custom presets).
 */
function getPresetResult(
  id: DateRangePresetId,
  tz: string,
  currentRange: DateRangeValue | null,
  currentPresetId: DateRangePresetId | null,
): { from: string; to: string } | null {
  if (isCustomPreset(id)) {
    if (currentPresetId === id && currentRange) {
      return {
        from: currentRange.from.toISOString(),
        to: currentRange.to.toISOString(),
      };
    }

    return null;
  }

  const range = getPresetRange(id, tz);
  if (!range) {
    return null;
  }

  return {
    from: range.from.toISOString(),
    to: range.to.toISOString(),
  };
}

/**
 * Tab content for the Custom Screen date range demo: selector, selected range display, and preset from/to results.
 */
export function CustomScreenDateRangeTab() {
  const tz = useTimezone();
  const [range, setRange] = useState<DateRangeValue | null>(null);
  const [presetId, setPresetId] = useState<DateRangePresetId | null>(null);

  const handleChange = (newRange: DateRangeValue, newPresetId: DateRangePresetId) => {
    setRange(newRange);
    setPresetId(newPresetId);
  };

  const presetResults = useMemo(
    () =>
      PRESET_MENU_ORDER.map((id) => {
        const result = getPresetResult(id, tz, range, presetId);
        const label = PRESET_LABELS[id];
        return { id, label, result };
      }),
    [tz, range, presetId],
  );

  return (
    <>
      <Stack spacing={3}>
        <DateRangeSelector value={range} presetId={presetId} onChange={handleChange} />
      </Stack>
      <Box sx={{ pt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Demo: rango seleccionado
        </Typography>
        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
          {range
            ? `from: ${range.from.toISOString()}\nto:   ${range.to.toISOString()}\npreset: ${presetId ?? '—'}`
            : '—'}
        </Typography>
      </Box>
      <Box sx={{ pt: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          From/To por tipo de filtro
        </Typography>
        <Stack component="ul" spacing={1} sx={{ listStyle: 'none', pl: 0, m: 0 }}>
          {presetResults.map(({ id, label, result }) => (
            <Box
              key={id}
              component="li"
              sx={{
                fontFamily: 'monospace',
                typography: 'caption',
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" component="span">
                {label}
              </Typography>
              {result ? (
                <Typography variant="body2" component="pre" sx={{ m: 0, mt: 0.25 }}>
                  {`  from: ${result.from}\n  to:   ${result.to}`}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                  —
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </Box>
    </>
  );
}
